import { useState } from 'react';
import { useUserPlan } from './UserPlanContext';
import { useT } from './LangContext';
import { C } from '../lib/colors';
import UpgradeModal from './UpgradeModal';
import { trackOnce } from '../lib/track';

export default function PaywallGate({ feature, children, fallback = null, inline = false, variant, teaseLine }) {
  const { canAccess, loading, plan } = useUserPlan();
  const { t } = useT();
  const [showUpgrade, setShowUpgrade] = useState(false);

  if (loading) return null;
  if (canAccess(feature)) return children;

  // ─── Fase 15-16 · Bloque 7 — Tease variant ───────────────────────────────
  // Render the real computed children behind a 7px blur + dark overlay with
  // an unlock CTA. Used in RESULTS for funded/stress/sizing teasers.
  if (variant === "tease") {
    return (
      <>
        <div data-testid="paywall-tease" style={{ position: "relative", marginTop: 28 }}>
          <div aria-hidden="true" style={{
                  filter: "blur(7px)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}>
            {children}
          </div>
          <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(180deg, rgba(20,22,24,0.40) 0%, rgba(20,22,24,0.80) 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexDirection: "column", gap: 14, padding: 20,
                  fontFamily: "var(--plex)", color: C.linen,
                  textAlign: "center",
                }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.brass} strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <div style={{ color: C.bone, fontSize: 14 }}>{t("tease_ready")}</div>
            {teaseLine && (
              <div style={{ color: C.linen, fontSize: 12.5, maxWidth: 460, lineHeight: 1.5 }}>
                {teaseLine}
              </div>
            )}
            <button onClick={() => { trackOnce(`paywall_viewed_${feature}`, { section: feature }); setShowUpgrade(true); }}
                    data-testid="tease-unlock-cta"
                    style={{
                      background: "transparent",
                      border: `1px solid ${C.brass}`,
                      color: C.brass,
                      padding: "8px 20px",
                      fontFamily: "var(--mono)", fontSize: 12, letterSpacing: 0.18,
                      cursor: "pointer",
                    }}>
              {t("tease_cta")}
            </button>
          </div>
        </div>
        {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
      </>
    );
  }

  if (fallback) return fallback;

  const style = inline ? {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    border: `1px dashed ${C.dust}`,
    background: 'rgba(42, 48, 50, 0.5)',
    color: C.smoke,
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: 11,
    cursor: 'pointer',
  } : {
    padding: 24,
    border: `1px dashed ${C.brass}`,
    background: C.archive,
    textAlign: 'center',
    fontFamily: "'IBM Plex Sans', sans-serif",
    color: C.linen,
  };

  return (
    <>
      <div style={style} onClick={() => setShowUpgrade(true)} data-testid="paywall-gate">
        {inline ? (
          <>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.brass} strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span style={{ color: C.brass }}>{t('paywall_pro_badge')}</span>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.brass} strokeWidth="1.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <div style={{ marginBottom: 8, fontSize: 14, color: C.bone }}>
              {t('paywall_pro_feature')}
            </div>
            <div style={{ fontSize: 12, fontStyle: 'italic', marginBottom: 16, color: C.smoke }}>
              {t('paywall_current_plan', { plan: plan.toUpperCase() })}
            </div>
            <button style={{
              background: 'transparent',
              border: `1px solid ${C.cinnabar}`,
              color: C.cinnabar,
              padding: '10px 22px',
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 12,
              letterSpacing: '0.12em',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
              {t('paywall_upgrade_cta')}
            </button>
          </>
        )}
      </div>
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
    </>
  );
}
